import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Chip,
  useTheme,
  useMediaQuery,
  Fade,
  Slide,
} from '@mui/material';
import {
  CheckCircle,
  Star,
  TrendingUp,
  Security,
  Support,
  ArrowForward,
  Apartment,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { DEFAULT_PAID_PLAN, normalizePaidPlanType } from '../modules/onboarding/services/onboardingService';
import { asaasService } from '../services/asaasService';
import { AppHeader } from '../components/layout/AppHeader';

interface UserSubscriptionStatus {
  isNewUser: boolean;
  hasExpiredPlan: boolean;
  lastPlanType?: string;
  expirationDate?: string;
}

const WelcomeToSubscriptionPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { authState } = useAuth();
  
  const [activeStep, setActiveStep] = useState(0);
  const [userStatus, setUserStatus] = useState<UserSubscriptionStatus>({
    isNewUser: true,
    hasExpiredPlan: false,
  });
  const [planPrice, setPlanPrice] = useState<string>('R$ 30,00');
  const [isLoading, setIsLoading] = useState(true);

  const steps = ['Bem-vindo', 'Benefícios', 'Confirmação'];

  // Check user subscription history
  useEffect(() => {
    const checkUserStatus = async () => {
      if (!authState.user?.email) {
        setIsLoading(false);
        return;
      }

      try {
        const access = await asaasService.checkAccessByEmail(authState.user.email);
        const sub: any = (access as any)?.subscription;
        const hasActive = Boolean((access as any)?.accessGranted);
        if (hasActive) {
          setUserStatus({ isNewUser: false, hasExpiredPlan: false });
        } else if (sub) {
          const status = (sub.status || '').toString().toUpperCase();
          const expired = status !== 'ACTIVE';
          const expirationDate = sub?.nextDueDate || undefined;
          setUserStatus({
            isNewUser: false,
            hasExpiredPlan: expired,
            lastPlanType: 'premium',
            expirationDate,
          });
        } else {
          setUserStatus({ isNewUser: true, hasExpiredPlan: false });
        }
      } catch (error) {
        console.error('Error checking user subscription status:', error);
        // Default to new user if we can't determine status
        setUserStatus({
          isNewUser: true,
          hasExpiredPlan: false,
        });
      } finally {
        setIsLoading(false);
      }
    };

    checkUserStatus();
  }, [authState.user?.email]);

  // Preço fixo (Asaas)
  useEffect(() => {
    setPlanPrice('R$ 30,00');
  }, []);

  const plan = normalizePaidPlanType(searchParams.get('plan')) || DEFAULT_PAID_PLAN;

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      // Add smooth transition delay
      setTimeout(() => {
        setActiveStep(activeStep + 1);
      }, 100);
    } else {
      // Go to payment
      navigate(`/payment?plan=${plan}`);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    } else {
      navigate('/dashboard');
    }
  };

  const getWelcomeMessage = () => {
    if (userStatus.hasExpiredPlan) {
      return {
        title: 'Bem-vindo ao MUU! 👋',
        subtitle: 'Sentimos sua falta! Que tal reativar seu plano?',
        description: userStatus.expirationDate 
          ? `Seu plano ${userStatus.lastPlanType === 'premium' ? 'Muu Plus' : 'Premium'} expirou em ${userStatus.expirationDate}. Reative agora e continue aproveitando todos os benefícios.`
          : 'Reative seu plano e continue aproveitando todos os benefícios da plataforma MUU.',
        alertType: 'warning' as const,
        alertMessage: 'Plano expirado - Reative para continuar usando todas as funcionalidades',
      };
    } else {
      return {
        title: 'Bem-vindo ao MUU! 🎉',
        subtitle: 'Estamos felizes em tê-lo conosco!',
        description: 'Para aproveitar ao máximo nossa plataforma de gestão pecuária, você precisa de um plano ativo. Vamos te mostrar todos os benefícios que preparamos para você.',
        alertType: 'info' as const,
        alertMessage: '',
      };
    }
  };

  const planFeatures = [
    { icon: <TrendingUp />, text: 'Gestão completa do rebanho' },
    { icon: <Security />, text: 'Controle de saúde e nutrição' },
    { icon: <CheckCircle />, text: 'Relatórios detalhados' },
    { icon: <Star />, text: 'Dashboard avançado' },
    { icon: <Apartment />, text: 'Múltiplas propriedades rurais' },
    { icon: <Support />, text: 'Suporte prioritário 24/7' },
  ];

  const renderStepContent = () => {
    const welcomeData = getWelcomeMessage();

    switch (activeStep) {
      case 0:
        return (
          <Fade in timeout={800} style={{ transitionTimingFunction: 'ease-in-out' }}>
            <Box>
              <Typography variant="h3" component="h1" gutterBottom align="center" sx={{ mb: 2 }}>
                {welcomeData.title}
              </Typography>
              <Typography variant="h6" color="text.secondary" align="center" sx={{ mb: 4 }}>
                {welcomeData.subtitle}
              </Typography>
              
              {welcomeData.alertMessage && (
                <Alert severity={welcomeData.alertType} sx={{ mb: 4 }}>
                  {welcomeData.alertMessage}
                </Alert>
              )}

              <Typography variant="body1" align="center" sx={{ mb: 4, lineHeight: 1.6 }}>
                {welcomeData.description}
              </Typography>

              <Box display="flex" justifyContent="center" sx={{ mb: 4 }}>
                <Card sx={{ maxWidth: 400, textAlign: 'center' }}>
                  <CardContent>
                    <Apartment sx={{ fontSize: '3rem', color: theme.palette.primary.main, mb: 2 }} />
                    <Typography variant="h5" gutterBottom>
                      Muu Plus
                    </Typography>
                    <Box display="flex" alignItems="baseline" justifyContent="center" gap={1} sx={{ mb: 2 }}>
                      <Typography variant="h4" color="primary" fontWeight="bold">
                        {planPrice}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">/mês</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          </Fade>
        );

      case 1:
        return (
          <Slide direction="left" in timeout={800} style={{ transitionTimingFunction: 'ease-in-out' }}>
            <Box>
              <Typography variant="h4" component="h2" gutterBottom align="center" sx={{ mb: 4 }}>
                O que você terá acesso:
              </Typography>
              
              <List sx={{ maxWidth: 600, mx: 'auto' }}>
                {planFeatures.map((feature, index) => (
                  <ListItem key={index} sx={{ py: 1.5 }}>
                    <ListItemIcon sx={{ color: theme.palette.primary.main }}>
                      {feature.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={feature.text}
                      primaryTypographyProps={{ variant: 'body1', fontWeight: 500 }}
                    />
                  </ListItem>
                ))}
              </List>

              <Box sx={{ mt: 4, p: 3, bgcolor: theme.palette.grey[50], borderRadius: 2 }}>
                <Typography variant="body2" align="center" color="text.secondary">
                  💡 <strong>Dica:</strong> Você pode cancelar a qualquer momento, sem taxas ou multas.
                </Typography>
              </Box>
            </Box>
          </Slide>
        );

      case 2:
        return (
          <Slide direction="left" in timeout={800} style={{ transitionTimingFunction: 'ease-in-out' }}>
            <Box>
              <Typography variant="h4" component="h2" gutterBottom align="center" sx={{ mb: 4 }}>
                Pronto para começar?
              </Typography>
              
              <Card sx={{ maxWidth: 500, mx: 'auto', mb: 4 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" gutterBottom>
                    Resumo do seu plano:
                  </Typography>
                  
                  <Box sx={{ my: 3 }}>
                    <Typography variant="h5" color="primary" fontWeight="bold">
                      Muu Plus - {planPrice}/mês
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Cobrança mensal • Cancele quando quiser
                    </Typography>
                  </Box>

                  <Alert severity="success" sx={{ mt: 2 }}>
                    🔒 Pagamento 100% seguro via Asaas
                  </Alert>
                </CardContent>
              </Card>

              <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 2 }}>
                Ao continuar, você será redirecionado para nossa página de pagamento segura.
              </Typography>
            </Box>
          </Slide>
        );

      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.grey[50] }}>
      <AppHeader />
      
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Stepper activeStep={activeStep} alternativeLabel={!isMobile}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Box sx={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          {renderStepContent()}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            onClick={handleBack}
            variant="outlined"
          >
            Voltar
          </Button>
          
          <Button
            onClick={handleNext}
            variant="contained"
            endIcon={<ArrowForward />}
            size="large"
          >
            {activeStep === steps.length - 1 ? 'Ir para Pagamento' : 'Próximo'}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default WelcomeToSubscriptionPage;
